import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ApiCall from '../utils/apicall';
import jwt_decode from 'jwt-decode';
import { showSuccessToast, showErrorToast } from '../utils/Toast';
import { setAccessToken, clearAccessToken } from '../utils/authToken';

export const loginUser = createAsyncThunk('login-user/auth', async (data, thunkAPI) => {
  try {
    const res = await ApiCall.post('/auth/login', data);
    const token = res.data.data;
    const decodedToken = jwt_decode(token.token);
    setAccessToken(token.token);
    return { token: token.token, decodedToken: decodedToken };
  } catch (error) {
    const { message } = error.response.data;
    throw new Error(message);
  }
});

export const getUsers = createAsyncThunk('get-users/users', async (data, thunkAPI) => {
  // console.log('here');
  try {
    const res = await ApiCall.get('/user');
    if (res.data.data.users) {
      return res.data.data.users;
    } else throw new Error('No User Found !');
  } catch (error) {
    console.log(error);
    return thunkAPI.rejectWithValue('Error logging in');
  }
});

export const createUser = createAsyncThunk('create-users/users', async (data, thunkAPI) => {
  try {
    // 2026-09-05: was '/user/signup' - the PUBLIC signup handler, which hardcodes
    // isActive:false, ignores roleId and never stamps a tenantId, so every user an
    // admin created here was born suspended, role-less and tenant-less and could
    // not log in. POST '/user' is the real admin handler (createUser).
    const res = await ApiCall.post('/user', data);
    if (res.success === false) {
      return thunkAPI.rejectWithValue(res.message);
    }
    return { ...res.data.data.user, roles: [{ id: data.roleId }] };
  } catch (error) {
    console.log(error);
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

export const updateUser = createAsyncThunk('update-users/users', async (data, thunkAPI) => {
  try {
    const res = await ApiCall.put(`/user/${data.id}`, data);
    if (res.status === false) {
      return thunkAPI.rejectWithValue(res.message);
    }
    return { ...res.data.data.updatedUser, roles: [{ id: data.roleId }] };
  } catch (error) {
    console.log(error);
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

export const deleteUser = createAsyncThunk('delete-users/users', async (data, thunkAPI) => {
  try {
    // console.log(data);
    const res = await ApiCall.delete(`/user/${data}`);
    if (res.data.success) {
      return data;
    }
  } catch (error) {
    return thunkAPI.rejectWithValue('Error deleting in');
  }
});

export const fetchMyPhoto = createAsyncThunk('users/fetchMyPhoto', async (_, thunkAPI) => {
  try {
    const res = await ApiCall.get('/user/me/photo');
    return res.data.data.photoUrl || null;
  } catch {
    return null; // silently ignore — photo is optional
  }
});

export const userSlice = createSlice({
  name: 'users',
  initialState: {
    token: '',
    loginUser: '',
    photoUrl: null,
    usersList: [],
    isLoading: false,
    isSuccess: false,
  },
  reducers: {
    logout: (state) => {
      clearAccessToken();
      state.loginUser = '';
      state.photoUrl = null;
      state.usersList = [];
      state.isLoading = false;
      state.isSuccess = false;
    },
    setPhotoUrl: (state, action) => {
      state.photoUrl = action.payload;
    },
    // Used by self-serve registration to set auth state after receiving a token directly
    setUserFromToken: (state, action) => {
      state.loginUser = action.payload;
      state.token = action.payload.token;
      state.isLoading = false;
      state.isSuccess = true;
    },
  },
  extraReducers: (builder) => {
    // ---------------Login User------------------
    builder.addCase(loginUser.pending, (state, action) => {
      state.isLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loginUser = action.payload;
      state.token = action.payload.token;
      state.isLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.isLoading = false;
      state.isSuccess = false;
          showErrorToast('Login failed. Check your credentials.');
    });

    // -------------Getting User-------------
    builder.addCase(getUsers.pending, (state, action) => {
      state.isLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(getUsers.fulfilled, (state, action) => {
      if (action.payload === 'undefined') return alert('no user found');
      if (action.payload.length > 0) {
        state.usersList = action.payload;
        state.isSuccess = true;
        state.isLoading = false;
        return;
      }
      state.usersList = [];
    });
    builder.addCase(getUsers.rejected, (state, action) => {
      state.isLoading = false;
      state.isSuccess = false;
          showErrorToast('Failed to load users. Please refresh.');
    });

    // ---------------------Creating User---------------------
    builder.addCase(createUser.pending, (state, action) => {
      state.isLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(createUser.fulfilled, (state, action) => {
      // console.log(action.payload)
      state.usersList.unshift(action.payload);
      state.isLoading = false;
      state.isSuccess = true;
          showSuccessToast('User created successfully.');
    });
    builder.addCase(createUser.rejected, (state, action) => {
      state.isLoading = false;
      state.isSuccess = false;
          showErrorToast('Failed to create user. Please try again.');
    });

    // ----------------------Update User----------------------
    builder.addCase(updateUser.pending, (state, action) => {
      state.isLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(updateUser.fulfilled, (state, action) => {
      const updatedUser = action.payload;
      state.usersList = state.usersList.map((user) => {
        if (user.id === updatedUser.id) {
          return { ...user, ...updatedUser };
        }
        return user;
      });
      state.isLoading = false;
      state.isSuccess = true;
          showSuccessToast('User updated successfully.');
    });
    builder.addCase(updateUser.rejected, (state, action) => {
      state.isLoading = false;
      state.isSuccess = false;
          showErrorToast('Failed to update user. Please try again.');
    });

    // ----------------------Delete user----------------------

    builder.addCase(deleteUser.pending, (state, action) => {
      state.isLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(deleteUser.fulfilled, (state, action) => {
      // console.log(action.payload);
      state.usersList = state.usersList.filter((user) => user.id !== action.payload);
      state.isLoading = false;
      state.isSuccess = true;
          showSuccessToast('User deleted successfully.');
    });
    builder.addCase(deleteUser.rejected, (state, action) => {
      state.isLoading = false;
      state.isSuccess = false;
          showErrorToast('Failed to delete user. Please try again.');
    });

    // ── Fetch profile photo ───────────────────────────────────────────
    builder.addCase(fetchMyPhoto.fulfilled, (state, action) => {
      state.photoUrl = action.payload;
    });
  },
});

export const { logout, setUserFromToken, setPhotoUrl } = userSlice.actions;
export default userSlice.reducer;
